import React, { useCallback, useRef } from "react";
import { ErrorMessage, useField } from "formik";
import styled from "styled-components";
import type { TooltipPlacement } from "antd/es/tooltip";
import { Tooltip } from "antd";

const OTP_LENGTH = 4;

export interface OtpFieldProps {
  label?: string;
  name: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  isRequired?: boolean;
  hideLabel?: boolean;
  labelMb?: number | string;
  labelWeight?: number | string;
  labelSize?: number | string;
  height?: string | number;
  inputBoxSize?: "small" | "medium" | "large";
  length?: number;
  variant?: "box" | "underline";
  showHelp?: boolean;
  helpText?: string;
  helpPlacement?: TooltipPlacement;
}

const FieldContainer = styled.div`
  font-family: "Poppins", sans-serif;
`;

const LabelRow = styled.div<{ $mb?: number | string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ $mb }) => ($mb !== undefined ? $mb : "4px")};
`;

const StyledLabel = styled.label<{
  $weight?: number | string;
  $size?: number | string;
}>`
  display: block;
  font-weight: ${({ $weight }) => ($weight !== undefined ? $weight : 600)};
  font-size: ${({ $size }) =>
    $size !== undefined
      ? typeof $size === "number"
        ? `${$size}px`
        : $size
      : "14px"};
`;

const StyledError = styled.div`
  color: #d32f2f;
  font-size: 0.8125rem;
  margin-top: 6px;
  font-weight: 500;
`;

const BoxesWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const StyledOtpBox = styled.input<{
  $error?: boolean;
  $height?: string | number;
  $inputBoxSize?: "small" | "medium" | "large";
  $variant?: "box" | "underline";
}>`
  width: 52px;
  max-width: 60px;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  padding: 0;
  text-align: center;

  font-size: ${({ $inputBoxSize, $variant }) => {
    if ($variant === "underline") return "24px";
    switch ($inputBoxSize) {
      case "small":
        return "15px";
      case "large":
        return "22px";
      default:
        return "19px";
    }
  }};
  font-family: "Poppins", sans-serif !important;
  font-weight: 600;
  color: ${({ $variant }) => ($variant === "underline" ? "#0a9fe2" : "#1a1a1a")};

  background-color: ${({ $variant }) => ($variant === "underline" ? "transparent" : "#fafafa")};
  border: ${({ $error, $variant }) =>
    $variant === "underline"
      ? "none"
      : `2px solid ${$error ? "#d32f2f" : "#e5e5e5"}`};
  border-bottom: ${({ $error, $variant }) =>
    $variant === "underline"
      ? `2px solid ${$error ? "#d32f2f" : "#e0e0e0"}`
      : undefined};
  border-radius: ${({ $variant }) => ($variant === "underline" ? 0 : "10px")};
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  height: ${({ $height }) =>
    $height !== undefined
      ? typeof $height === "number"
        ? `${$height}px`
        : $height
      : "52px"};

  &::placeholder {
    color: #b0b0b0 !important;
    font-weight: 400;
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${({ $error, $variant }) =>
      $variant === "underline" ? undefined : $error ? "#b71c1c" : "#cfcfcf"};
    border-bottom-color: ${({ $error, $variant }) =>
      $variant === "underline" ? ($error ? "#d32f2f" : "#bdbdbd") : undefined};
    background-color: ${({ $variant }) => ($variant === "underline" ? "transparent" : "#f5f5f5")};
  }

  &:focus {
    border-color: ${({ $error, $variant }) =>
      $variant === "underline" ? undefined : $error ? "#d32f2f" : "#0a9fe2"} !important;
    border-bottom-color: ${({ $error, $variant }) =>
      $variant === "underline" ? ($error ? "#d32f2f" : "#0a9fe2") : undefined} !important;
    box-shadow: ${({ $error, $variant }) =>
      $variant === "underline"
        ? "none"
        : $error
          ? "0 0 0 3px rgba(211, 47, 47, 0.15)"
          : "0 0 0 3px rgba(10, 159, 226, 0.15)"} !important;
    background-color: ${({ $variant }) => ($variant === "underline" ? "transparent" : "#fff")} !important;
    outline: none;
  }

  &:disabled {
    background-color: ${({ $variant }) => ($variant === "underline" ? "transparent" : "#f0f0f0")};
    color: #9e9e9e;
    cursor: not-allowed;
  }
`;

const HelpIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #cfcfcf;
  border-radius: 50%;
  font-size: 11px;
  color: #8b8b8b;
  cursor: pointer;
  user-select: none;
`;

export const OtpField: React.FC<OtpFieldProps> = ({
  label = "Enter OTP",
  name,
  value: controlledValue,
  onChange: controlledOnChange,
  onBlur: controlledOnBlur,
  disabled = false,
  isRequired = false,
  hideLabel = false,
  labelMb,
  labelWeight,
  labelSize = 14,
  height,
  inputBoxSize = "medium",
  length = OTP_LENGTH,
  variant = "box",
  showHelp = false,
  helpText,
  helpPlacement = "topRight",
}) => {
  const [field, meta, helpers] = useField(name);
  const value = controlledValue !== undefined ? controlledValue : field.value;
  const maxLength = Math.max(1, length);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const digits = (value ?? "").replace(/\D/g, "").slice(0, maxLength).split("");
  while (digits.length < maxLength) digits.push("");

  const setOtpValue = useCallback(
    (newValue: string) => {
      const digitsOnly = newValue.replace(/\D/g, "").slice(0, maxLength);
      helpers.setValue(digitsOnly);
      if (controlledOnChange) {
        const syntheticEvent = {
          target: { name, value: digitsOnly },
        } as React.ChangeEvent<HTMLInputElement>;
        controlledOnChange(syntheticEvent);
      }
    },
    [helpers, maxLength, name, controlledOnChange]
  );

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    const newValue = next.join("");
    setOtpValue(newValue);
    if (digit && index < maxLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      const next = [...digits];
      next[index - 1] = "";
      setOtpValue(next.join(""));
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < maxLength - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, maxLength);
    if (!pasted) return;
    setOtpValue(pasted);
    const nextIndex = Math.min(pasted.length, maxLength - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleBoxBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(e);
    controlledOnBlur?.(e);
  };

  return (
    <FieldContainer>
      {!hideLabel && (
        <LabelRow $mb={labelMb}>
          <StyledLabel htmlFor={name} $weight={labelWeight} $size={labelSize}>
            {label}
            {isRequired && (
              <span style={{ color: "red", fontSize: "15px" }}>*</span>
            )}
          </StyledLabel>
          {showHelp && !!helpText && (
            <Tooltip
              title={helpText}
              placement={helpPlacement}
              zIndex={2000}
            >
              <HelpIcon aria-label="help">?</HelpIcon>
            </Tooltip>
          )}
        </LabelRow>
      )}

      <BoxesWrapper
        role="group"
        aria-label={label}
        onPaste={handlePaste}
      >
        {digits.map((digit, index) => (
          <StyledOtpBox
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            id={index === 0 ? name : undefined}
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            value={digit}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onBlur={handleBoxBlur}
            disabled={disabled}
            maxLength={1}
            $error={!!(meta.touched && meta.error)}
            $height={height}
            $inputBoxSize={inputBoxSize}
            $variant={variant}
            aria-label={`Digit ${index + 1} of ${maxLength}`}
          />
        ))}
      </BoxesWrapper>

      <ErrorMessage name={name} component={StyledError} />
    </FieldContainer>
  );
};
